import { Divider, Grid, Typography } from '@material-ui/core';
import ExpandMore from '@material-ui/icons/ExpandMore';
import Search from '@material-ui/icons/Search';
import DeleteIcon from '@material-ui/icons/Delete';
import PersonAddIcon from '@material-ui/icons/PersonAdd';
import clsx from 'clsx';
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { batch, connect, useSelector } from 'react-redux';
import BaseButton from 'src/common/components/base-button';
import { setNumberWithCommas } from 'src/common/helpers/text-formatting-helper';
import { IElectronicJournalRecord } from 'src/common/interfaces/states/IElectronicJournalRecord';
import { ReactComponent as EmptyCartIcon } from 'src/common/media/EmptyCart.svg';
import { ReactComponent as LoyaltyIcon } from 'src/common/media/Loyalty.svg';
import { AppState, store } from 'src/common/redux/configure-store';
import { ItemListActions } from 'src/common/redux/reducers/item-list-reducer';
import { MiddleAreaActions } from 'src/common/redux/reducers/middle-area-reducer';
import { getProductCount } from 'src/total/business-logic/product-count';
import { handleCustomerLoyaltySearch } from '../business-logic/handle-customer-loyalty-search';
import { styles } from './customer-loyalty-styles';
import { replaceCustomerInCart } from '../business-logic/set-customer';
import { removeCustomerHandler } from 'src/home-page/business-logic/remove-loyalty-customer';
import { ITransactionState } from 'src/common/interfaces/states/ITransactionState';
import { getCurrentXLRConfig } from 'src/xlr-configuration/xlr-config';

interface ICustomerLoyalty {
  isCustomerDisplay?: boolean;
}
type Props = ReturnType<typeof mapStateToProps> & ICustomerLoyalty;

const CustomerLoyalty: React.FC<Props> = (props: Props): JSX.Element => {
  const classes = styles();

  const ejItems = useSelector(
    (state: AppState): IElectronicJournalRecord[] => state.itemList.ejItems,
  );
  const isTendering = useSelector((state: AppState): boolean => state.tendering.isTendering);
  const disableLoyaltySearchOnTenderMode = getCurrentXLRConfig().disableLoyaltySearchOnTenderMode;

  const returnModeActive = useSelector(
    (state: AppState): boolean => !!state.posMode.returnMode.returnType,
  );
  const { t } = useTranslation();
  const customerPoints: number | undefined =
    props.transactionState.transactionCustomer?.points ?? 0;
  const cartHasCustomer: boolean = !!props.transactionState.transactionCustomer;
  const firstName = props.transactionState.transactionCustomer?.firstName || '';
  const lastName = props.transactionState.transactionCustomer?.lastName || '';
  const transactionCustomerName: string =
    firstName || lastName ? `${firstName} ${lastName}` : t('WithLoyalty');

  const [isControlHidden, setIsControlHidden] = useState(true);

  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleClickOutside = event => {
      if (ref.current && !ref.current.contains(event.target)) {
        setIsControlHidden(true);
      }
    };
    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, []);

  const customerSearchClickHandler = (): void => {
    if (cartHasCustomer) {
      setIsControlHidden(!isControlHidden);
    } else {
      handleCustomerLoyaltySearch();
    }
  };

  const loyaltyBarClickHandler = (): void => {
    if (cartHasCustomer) {
      batch((): void => {
        store.dispatch(ItemListActions.selectItem(-1));
        store.dispatch(MiddleAreaActions.showActionWindow());
      });
    } else {
      handleCustomerLoyaltySearch();
    }
  };

  const removeCustomer = async (): Promise<void> => {
    setIsControlHidden(true);
    removeCustomerHandler();
  };

  const replaceCustomerHandler = (): void => {
    setIsControlHidden(true);
    replaceCustomerInCart();
  };

  const searchLoyaltyButton = (
    <Grid
      container
      item
      className={`${classes.loyaltySearch}`}
      onClick={customerSearchClickHandler}
    >
      {cartHasCustomer ? (
        <ExpandMore
          className={clsx({
            [classes.loyaltyIconRotated]: !isControlHidden,
          })}
        />
      ) : (
        <Search />
      )}
    </Grid>
  );

  const itemCount = () => {
    const count = getProductCount(ejItems, returnModeActive, true);
    const localizedCountLabel = count > 1 ? 'LoyaltyMultipleItemCount' : 'LoyaltySingleItemCount';

    return (
      <Grid
        container
        className={`${classes.loyaltyItemsContainer} ${classes.loyaltySelectedFont} fullHeight`}
        alignItems='center'
        justifyContent={'flex-end'}
        data-testid={`loyalty-item-count`}
      >
        <EmptyCartIcon className={classes.loyaltyIcon} />
        {t(localizedCountLabel, { arg: count })}
      </Grid>
    );
  };

  return (
    <Grid
      container
      className={`fullHeight ${classes.container} ${classes.loyaltyContainerWrapper}`}
      ref={ref}
    >
      <Grid wrap='nowrap' container item className={clsx('fullHeight', classes.titleBarWrapper)}>
        <BaseButton
          buttonType='white'
          centerRipple={false}
          className={classes.loyaltyTitleButton}
          onClick={!props.isCustomerDisplay ? loyaltyBarClickHandler : undefined}
          variant='text'
          data-testid={`search-loyalty-bar`}
          disabled={(isTendering && disableLoyaltySearchOnTenderMode) || returnModeActive}
        >
          <Grid
            container
            item
            className={clsx(classes.loyaltyTitleButton, classes.loyaltyTitleButtonLabel, {
              [classes.loyaltySelectedFont]: cartHasCustomer,
            })}
          >
            <div className={classes.loyaltyIcon} data-testid='loyalty-card-icon'>
              <LoyaltyIcon />
            </div>
            <Grid
              item
              className={clsx(classes.loyaltyTitle, {
                [classes.loyaltySelectedFont]: cartHasCustomer,
              })}
              data-testid={`loyalty-label-wrapper`}
            >
              {cartHasCustomer ? (
                <Grid container direction='column' alignContent='center'>
                  <p className={classes.loyaltyDetails} data-testid={`loyalty-label`}>
                    {!props.isCustomerDisplay ? transactionCustomerName : t('LoyaltyAdded')}
                  </p>
                  {!props.isCustomerDisplay && (customerPoints ?? 0) >= 0 && (
                    <Typography
                      className={`${classes.loyaltyDetails} ${classes.loyaltyPoints}`}
                      data-testid='loyalty-bar-points'
                    >
                      {setNumberWithCommas(customerPoints)} {t('FooterPanelView_Points')}
                    </Typography>
                  )}
                </Grid>
              ) : (
                <Typography variant='body2'>{t('NoLoyalty')}</Typography>
              )}
            </Grid>
          </Grid>
        </BaseButton>
        <BaseButton
          buttonType='white'
          variant='contained'
          centerRipple={false}
          className={clsx(
            classes.loyaltySearchButton,
            props.isCustomerDisplay && classes.itemCountButton,
          )}
          onClick={customerSearchClickHandler}
          data-testid={`search-loyalty-button`}
          disabled={(isTendering && disableLoyaltySearchOnTenderMode) || returnModeActive}
        >
          {props.isCustomerDisplay ? itemCount() : searchLoyaltyButton}
        </BaseButton>
      </Grid>
      {!isControlHidden && (
        <Grid container item className={classes.removeReplaceWrapper}>
          <Divider />
          <BaseButton
            startIcon={<DeleteIcon className={classes.buttonIcons} />}
            buttonType='alternate'
            centerRipple={false}
            data-testid='remove-customer-button'
            onClick={() => removeCustomer()}
          >
            <Typography>{t('QSRComboItemClear')}</Typography>
          </BaseButton>
          <BaseButton
            startIcon={<PersonAddIcon className={classes.buttonIcons} />}
            buttonType='alternate'
            centerRipple={false}
            data-testid='replace-customer-button'
            onClick={() => replaceCustomerHandler()}
          >
            <Typography>{t('Replace')}</Typography>
          </BaseButton>
        </Grid>
      )}
    </Grid>
  );
};

interface TransactonProps {
  transactionState: ITransactionState;
}
const mapStateToProps = (state: AppState): TransactonProps => ({
  transactionState: state.transaction,
});

export default connect(mapStateToProps)(CustomerLoyalty);
